import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Container } from 'reactstrap';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FiBell, FiMenu, FiX } from 'react-icons/fi';
import { useBranding } from '../../../../contexts/BrandingContext';
import { useTranslation } from '../../../../contexts/LanguageContext';
import notificationService from '../../../../services/notificationService';
import { sx } from '../styles';

// Relative luminance (0 dark → 1 light) for contrast-aware text.
const luminance = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return 0.2;
  const [r, g, b] = [m[1], m[2], m[3]].map((c) => {
    const v = parseInt(c, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const PublicHeader = ({ scrolled }) => {
  const branding = useBranding();
  const { t } = useTranslation();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  // Drawer open state — desktop renders inline, mobile renders behind a menu
  // toggle. Close on route change so navigating inside the drawer hides it.
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the mobile drawer whenever the route changes. Plain dependency on
  // `location.pathname` — no race with lazy-loaded pages.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll while the drawer is open so the background doesn't
  // scroll underneath on iOS.
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (drawerOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  // Poll the unread-count endpoint every 60s while the user is authenticated.
  // Cheap, good enough for a demo; swap for a WebSocket push once real-time
  // features land.
  useEffect(() => {
    if (!isAuthenticated) { setUnreadCount(0); return undefined; }
    let cancelled = false;
    const fetchCount = () => {
      notificationService.getUnreadCount()
        .then((r) => { if (!cancelled) setUnreadCount(r.data?.count || 0); })
        .catch(() => { /* non-fatal */ });
    };
    fetchCount();
    const handle = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [isAuthenticated]);

  // Airbnb-style header — always white, dark text. Keeps the brand
  // palette out of the chrome so the centered logo + nav read clean
  // regardless of the configured primary colour.
  const bg = branding.primary_color || '#7C3AED';
  // Reserved for the ``border`` underneath when not scrolled — the
  // luminance helper is used elsewhere to pick a contrasting tone.
  // eslint-disable-next-line no-unused-vars
  const bgIsLight = luminance(bg) > 0.6;
  const headerBg = '#ffffff';
  const lightChrome = false;
  const textColor = '#1a1a1a';
  const subtleTextColor = '#555';

  // Short initials for avatar.
  const initials = (user?.full_name || user?.name || user?.phone || 'U')
    .toString()
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const navLinkStyle = (mobile = false) => ({
    color: mobile ? '#1a1a1a' : subtleTextColor,
    textDecoration: 'none',
    fontWeight: mobile ? 600 : 500,
    fontSize: mobile ? '1rem' : '0.88rem',
    padding: mobile ? '12px 0' : undefined,
    display: mobile ? 'block' : undefined,
    borderBottom: mobile ? '1px solid #f0f0f0' : undefined,
  });

  // Authenticated link set — reused between inline desktop nav and the
  // mobile drawer so the two stay in sync.
  const authLinks = [
    { to: '/dashboard', label: t('page.public_header.dashboard', 'Dashboard') },
    { to: '/bookings', label: t('page.public_header.myBookings', 'My Bookings') },
    { to: '/notifications', label: t('page.public_header.notifications', 'Notifications') },
    { to: '/profile', label: t('page.public_header.profile', 'Profile') },
  ];
  // Airbnb-style centered nav (anonymous only). Authenticated users
  // jump straight to dashboard chrome, so we skip the marketing tabs
  // and reuse the right-aligned account chip.
  const centerLinks = [
    { to: '/search', label: t('page.public_header.browse', 'Browse') },
    { to: '/find-warehouse', label: t('page.public_header.findWarehouse', 'Find My Warehouse') },
    { to: '/how-it-works', label: t('page.public_header.howItWorks', 'How It Works') },
    { to: '/cost-calculator', label: t('page.public_header.costCalculator', 'Cost Calculator') },
    { to: '/blog', label: t('page.public_header.blog', 'Blog') },
    { to: '/pricing', label: t('page.public_header.pricing', 'Pricing') },
  ];
  const centerLinkStyle = (active) => ({
    color: active ? '#7C3AED' : '#1a1a1a',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '0.92rem',
    padding: '8px 14px',
    borderRadius: 999,
    transition: 'background .15s, color .15s',
    background: active ? 'rgba(124, 58, 237, 0.08)' : 'transparent',
  });

  return (
    <div style={{
      ...sx.stickyNav,
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      background: headerBg,
      borderBottom: '1px solid #eee',
    }}>
      <Container>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          alignItems: 'center',
          gap: 16,
        }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            {/* Icon + wordmark pair (both transparent PNGs). */}
            <img
              src={branding.logo_url || '/logo-icon.png'}
              alt=""
              style={{ height: 40, objectFit: 'contain' }}
            />
            <img
              src={branding.logo_wordmark_url || '/logo-wordmark.png'}
              alt={branding.platform_name || 'Khulja Sim Sim'}
              style={{
                height: 26, objectFit: 'contain',
              }}
            />
          </Link>

          {/* ——— Centered nav (Airbnb-style, desktop only, anon only) ——— */}
          <div
            className="d-none d-md-flex"
            style={{ alignItems: 'center', gap: 4, justifyContent: 'center' }}
          >
            {!isAuthenticated && centerLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                style={centerLinkStyle(location.pathname === l.to)}
              >
                {l.label}
              </Link>
            ))}
          </div>

          {/* ——— Desktop (≥768px) right-aligned actions ——— */}
          <div className="d-none d-md-flex" style={{ alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" style={navLinkStyle()}>{t('page.public_header.dashboard', 'Dashboard')}</Link>
                <Link to="/bookings" style={navLinkStyle()}>{t('page.public_header.myBookings', 'My Bookings')}</Link>
                <Link to="/notifications" title={t('page.public_header.notifications', 'Notifications')} style={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40, height: 40, borderRadius: '50%',
                  background: lightChrome ? 'rgba(255,255,255,0.18)' : '#f3f4f6',
                  color: textColor,
                  textDecoration: 'none',
                }}>
                  <FiBell size={18} />
                  {unreadCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: -2, right: -2,
                      minWidth: 18, height: 18, padding: '0 5px',
                      borderRadius: 9,
                      background: '#ef4444', color: '#fff',
                      fontSize: '0.68rem', fontWeight: 700,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      border: `2px solid ${lightChrome ? bg : '#fff'}`,
                    }}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
                <Link to="/profile" style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 14px 6px 6px', borderRadius: 999,
                  background: lightChrome ? 'rgba(255,255,255,0.18)' : '#f3f4f6',
                  color: textColor,
                  textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem',
                }}>
                  <span style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: branding.accent_color || '#A78BFA',
                    color: '#fff', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '0.72rem',
                  }}>{initials}</span>
                  {user?.full_name || user?.name || t('page.public_header.account', 'Account')}
                </Link>
              </>
            ) : (
              <>
                <Link to="/register" id="list-warehouse-btn" data-track="list_warehouse_click" data-track-source="header" style={navLinkStyle()}>{t('page.public_header.listWarehouse', 'List Your Warehouse')}</Link>
                <Link to="/login" style={{
                  padding: '8px 20px', borderRadius: 999,
                  // Longhand instead of ``border:`` shorthand so React's
                  // diff against neighbouring Links (which only set
                  // ``borderBottom``) doesn't warn about a mixed
                  // shorthand/longhand collision on rerender.
                  borderWidth: '1.5px', borderStyle: 'solid', borderColor: '#1a1a1a',
                  color: '#1a1a1a',
                  textDecoration: 'none', fontWeight: 700, fontSize: '0.88rem',
                }}>{t('page.public_header.login', 'Login')}</Link>
                <Link to="/register" style={{
                  padding: '9px 22px', borderRadius: 999,
                  background: branding.primary_color || '#7C3AED',
                  color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: '0.88rem',
                  boxShadow: '0 4px 14px rgba(124, 58, 237, 0.35)',
                }}>{t('page.public_header.registerFree', 'Register Free')}</Link>
              </>
            )}
          </div>

          {/* ——— Mobile (<768px) ———
              Anonymous users see compact "Login" + "Register" buttons
              inline so the primary CTAs are reachable in one tap (no
              hamburger needed). Authenticated users keep the bell +
              hamburger pattern because they have many more
              destinations to choose from. */}
          <div className="d-flex d-md-none" style={{ alignItems: 'center', gap: 8 }}>
            {isAuthenticated ? (
              <>
                <Link to="/notifications" aria-label={t('page.public_header.notifications', 'Notifications')} style={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  width: 40, height: 40, borderRadius: '50%',
                  background: lightChrome ? 'rgba(255,255,255,0.18)' : '#f3f4f6',
                  color: textColor, textDecoration: 'none',
                }}>
                  <FiBell size={18} />
                  {unreadCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: -2, right: -2,
                      minWidth: 18, height: 18, padding: '0 5px',
                      borderRadius: 9,
                      background: '#ef4444', color: '#fff',
                      fontSize: '0.68rem', fontWeight: 700,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      border: `2px solid ${lightChrome ? bg : '#fff'}`,
                    }}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
                <button
                  type="button"
                  aria-label={t('page.public_header.openMenu', 'Open menu')}
                  aria-expanded={drawerOpen}
                  onClick={() => setDrawerOpen(true)}
                  style={{
                    width: 44, height: 44, borderRadius: 10,
                    background: lightChrome ? 'rgba(255,255,255,0.18)' : '#f3f4f6',
                    color: textColor, border: 'none',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <FiMenu size={20} />
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  style={{
                    padding: '7px 14px',
                    borderRadius: 8,
                    borderWidth: '1.5px', borderStyle: 'solid', borderColor: textColor,
                    color: textColor,
                    textDecoration: 'none',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('page.public_header.login', 'Login')}
                </Link>
                <Link
                  to="/register"
                  style={{
                    padding: '7px 14px',
                    borderRadius: 999,
                    background: branding.primary_color || '#7C3AED',
                    color: '#fff',
                    textDecoration: 'none',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.35)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('page.public_header.register', 'Register')}
                </Link>
                {/* Hamburger → drawer with the rest of the secondary
                    nav (List Your Warehouse, etc.). Login + Register
                    are duplicated inside the drawer too for users who
                    open it instead of tapping the inline buttons. */}
                <button
                  type="button"
                  aria-label={t('page.public_header.openMenu', 'Open menu')}
                  aria-expanded={drawerOpen}
                  onClick={() => setDrawerOpen(true)}
                  style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: lightChrome ? 'rgba(255,255,255,0.18)' : '#f3f4f6',
                    color: textColor, border: 'none',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <FiMenu size={18} />
                </button>
              </>
            )}
          </div>
        </div>
      </Container>

      {/* ——— Mobile drawer (rendered only when open) ———
           Portalled to <body> because the parent header has
           `backdrop-filter: blur(12px)`, which creates a containing
           block that would otherwise clip our `position: fixed`
           drawer + scrim to the navbar's height. */}
      {drawerOpen && createPortal(
        <>
          {/* Scrim — tap outside to close */}
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)',
              zIndex: 1039, cursor: 'pointer',
            }}
          />
          <div
            role="dialog"
            aria-label={t('page.public_header.mainMenu', 'Main menu')}
            className="d-md-none"
            style={{
              position: 'fixed',
              top: 0, right: 0, bottom: 0,
              width: 'min(84vw, 320px)',
              background: '#fff',
              boxShadow: '-10px 0 24px rgba(15, 23, 42, 0.15)',
              padding: '16px 20px 24px',
              overflowY: 'auto',
              zIndex: 1045,
            }}
          >
            <div style={{
              display: 'flex', justifyContent: 'flex-end',
              marginBottom: 12,
            }}>
              <button
                type="button"
                aria-label={t('page.public_header.closeMenu', 'Close menu')}
                onClick={() => setDrawerOpen(false)}
                style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: '#f3f4f6', border: 'none', color: '#1a1a1a',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <FiX size={20} />
              </button>
            </div>
            {isAuthenticated ? (
              <>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '8px 0 16px', borderBottom: '1px solid #f0f0f0',
                }}>
                  <span style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: branding.accent_color || '#A78BFA',
                    color: '#fff', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '0.85rem',
                  }}>{initials}</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#111827' }}>
                      {user?.full_name || user?.name || t('page.public_header.account', 'Account')}
                    </div>
                    <div className="small text-muted">{user?.phone || ''}</div>
                  </div>
                </div>
                {authLinks.map((l) => (
                  <Link key={l.to + l.label} to={l.to} style={navLinkStyle(true)}>{l.label}</Link>
                ))}
              </>
            ) : (
              <>
                {/* Primary auth actions at the TOP of the drawer so
                    they're the first thing the user sees when opening
                    the menu. Previously these were tucked in below
                    the secondary nav, which made them feel hidden. */}
                <Link to="/login" style={navLinkStyle(true)}>
                  {t('page.public_header.login', 'Login')}
                </Link>
                <Link to="/register" style={navLinkStyle(true)}>
                  {t('page.public_header.signup', 'Sign up')}
                </Link>
                <Link to="/register" data-track="list_warehouse_click" data-track-source="mobile_menu" style={navLinkStyle(true)}>
                  {t('page.public_header.listWarehouse', 'List Your Warehouse')}
                </Link>

                {/* Big-button CTAs kept below as the visual anchor —
                    still useful so the user can register without
                    fishing for the tappable text link. */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 }}>
                  <Link to="/login" style={{
                    padding: '12px 22px', borderRadius: 10,
                    border: '2px solid #1a1a1a', color: '#1a1a1a',
                    textDecoration: 'none', fontWeight: 700, fontSize: '0.95rem',
                    textAlign: 'center',
                  }}>{t('page.public_header.login', 'Login')}</Link>
                  <Link to="/register" style={{
                    padding: '12px 22px', borderRadius: 999,
                    background: branding.primary_color || '#7C3AED',
                    color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: '0.95rem',
                    textAlign: 'center',
                    boxShadow: '0 4px 14px rgba(124, 58, 237, 0.35)',
                  }}>{t('page.public_header.registerFree', 'Register Free')}</Link>
                </div>
              </>
            )}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};

export const AuthHeader = () => {
  const branding = useBranding();
  const { t } = useTranslation();
  return (
    <div style={{ ...sx.stickyNav, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
      <Container>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img
              src={branding.logo_url || '/logo-icon.png'}
              alt=""
              style={{ height: 36, objectFit: 'contain' }}
            />
            <img
              src={branding.logo_wordmark_url || '/logo-wordmark.png'}
              alt={branding.platform_name || 'Khulja Sim Sim'}
              style={{
                height: 22, objectFit: 'contain',
              }}
            />
          </div>
          <Link to="/dashboard" style={{
            padding: '7px 20px', borderRadius: 999,
            background: branding.primary_color || '#7C3AED',
            color: '#fff', textDecoration: 'none', fontWeight: 600, fontSize: '0.88rem',
          }}>{t('page.public_header.goToDashboard', 'Go to Dashboard')}</Link>
        </div>
      </Container>
    </div>
  );
};
